import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Crown, Users, Calendar, CheckCircle2, ArrowRight, Star, Zap, 
  Gift, Percent, MessageSquare, Sparkles, ExternalLink, User, Clock,
  Award, Heart, Target
} from 'lucide-react';
import { toStringArray } from '@/lib/utils';

const API_URL = window.location.origin;

const SDCHubPage = () => {
  const { user } = useAuth();
  const [hubData, setHubData] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectCoachDialog, setSelectCoachDialog] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [bookingCoach, setBookingCoach] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hubRes, coachesRes] = await Promise.all([
        axios.get(`${API_URL}/api/sdc/hub`),
        axios.get(`${API_URL}/api/coaches`)
      ]);
      setHubData(hubRes.data);
      setCoaches(coachesRes.data);
    } catch (error) {
      console.error('Failed to fetch SDC hub data:', error);
      toast.error('Failed to load SDC Hub');
    } finally {
      setLoading(false);
    }
  };

  const handleBookChemistry = async (coachId) => {
    setBookingCoach(coachId);
    try {
      const response = await axios.post(`${API_URL}/api/sdc/book-chemistry`, { coach_id: coachId });
      if (response.data.calendly_link) {
        window.open(response.data.calendly_link, '_blank');
        toast.success('Opening scheduling page...');
        fetchData(); // Refresh data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book chemistry session');
    } finally {
      setBookingCoach(null);
    }
  };

  const handleSelectCoach = async () => {
    if (!selectedCoach) return;
    
    try {
      await axios.post(`${API_URL}/api/sdc/select-coach`, { coach_id: selectedCoach });
      toast.success('Coach selected successfully!');
      setSelectCoachDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select coach');
    }
  };

  const handleBookCoachSession = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/sdc/book-coach-session`);
      if (response.data.calendly_link) {
        window.open(response.data.calendly_link, '_blank');
        toast.success('Opening scheduling page...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book session');
    }
  };

  const handleBookWonderTwin = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/sdc/book-wonder-twin`);
      if (response.data.calendly_link) {
        window.open(response.data.calendly_link, '_blank');
        toast.success('Opening scheduling page...');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book Wonder Twin session');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
        </div>
      </DashboardLayout>
    );
  }

  const chemistrySessionsUsed = hubData?.chemistry_sessions?.length || 0;
  const chemistrySessionsRemaining = 3 - chemistrySessionsUsed;
  const coachSelected = hubData?.selected_coach_id;
  const coachSessionsRemaining = hubData?.coach_sessions_remaining ?? 12;
  const wonderTwinSessionsRemaining = hubData?.wonder_twin_sessions_remaining ?? 3;
  const selectedCoachData = coachSelected ? coaches.find(c => c.id === coachSelected) : null;

  // Get coaches available for chemistry (not yet had session with)
  const bookedCoachIds = hubData?.chemistry_sessions?.map(s => s.coach_id) || [];
  const availableForChemistry = coaches.filter(c => !bookedCoachIds.includes(c.id) && c.id !== 'founder-crystal-nicole');

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="sdc-hub-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-terracotta to-forest flex items-center justify-center">
              <Crown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-forest">Sister Diamond Collective Hub</h1>
              <Badge className="bg-terracotta text-white">
                VIP Experience • $25,000 Container
              </Badge>
            </div>
          </div>
          <p className="text-gray-600 mt-2">Your exclusive VIP transformation journey with premium coaching and perks.</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{chemistrySessionsUsed}/3</p>
              <p className="text-xs text-gray-500">Chemistry Sessions</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{coachSessionsRemaining}</p>
              <p className="text-xs text-gray-500">Coach Sessions</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Star className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">{wonderTwinSessionsRemaining}</p>
              <p className="text-xs text-gray-500">Wonder Twin Sessions</p>
            </CardContent>
          </Card>
          <Card className="card-base">
            <CardContent className="p-4 text-center">
              <Percent className="h-6 w-6 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-forest">10%</p>
              <p className="text-xs text-gray-500">VIP Discount</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-sand/50 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="chemistry">Chemistry Sessions</TabsTrigger>
            <TabsTrigger value="coaching">Coaching</TabsTrigger>
            <TabsTrigger value="wondertwin">Wonder Twin</TabsTrigger>
            <TabsTrigger value="perks">VIP Perks</TabsTrigger>
            <TabsTrigger value="lounge">SDC Lounge</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Journey Progress */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Zap className="h-5 w-5 text-terracotta" />
                    Your SDC Journey
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Step 1: Chemistry Sessions */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    {chemistrySessionsUsed >= 3 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-terracotta flex items-center justify-center text-xs font-bold text-terracotta">1</div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-forest">Complete Chemistry Sessions</p>
                      <p className="text-xs text-gray-500">{chemistrySessionsUsed}/3 sessions with different coaches</p>
                      <Progress value={(chemistrySessionsUsed / 3) * 100} className="h-1 mt-2" />
                    </div>
                  </div>

                  {/* Step 2: Select Coach */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    {coachSelected ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-bold text-gray-400">2</div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-forest">Select Your Coach</p>
                      <p className="text-xs text-gray-500">
                        {coachSelected 
                          ? `Selected: ${selectedCoachData?.name || 'Coach'}` 
                          : 'Choose your coach after chemistry sessions'}
                      </p>
                    </div>
                    {chemistrySessionsUsed >= 3 && !coachSelected && (
                      <Button size="sm" className="btn-secondary text-xs" onClick={() => setSelectCoachDialog(true)}>
                        Select Coach
                      </Button>
                    )}
                  </div>

                  {/* Step 3: Coaching Sessions */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    {coachSessionsRemaining === 0 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-bold text-gray-400">3</div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-forest">Complete Coaching Sessions</p>
                      <p className="text-xs text-gray-500">{12 - coachSessionsRemaining}/12 sessions complete</p>
                      <Progress value={((12 - coachSessionsRemaining) / 12) * 100} className="h-1 mt-2" />
                    </div>
                  </div>

                  {/* Step 4: Wonder Twin Sessions */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-sand/30">
                    {wonderTwinSessionsRemaining === 0 ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs font-bold text-gray-400">4</div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-forest">Wonder Twin Sessions</p>
                      <p className="text-xs text-gray-500">{3 - wonderTwinSessionsRemaining}/3 sessions with Crystal & Dr. Nicole</p>
                      <Progress value={((3 - wonderTwinSessionsRemaining) / 3) * 100} className="h-1 mt-2" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Benefits Summary */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Gift className="h-5 w-5 text-terracotta" />
                    Your SDC Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { icon: Users, text: '3 Chemistry Sessions with different coaches', done: chemistrySessionsUsed >= 3 },
                    { icon: Calendar, text: '12 One-Hour Coaching Sessions', done: coachSessionsRemaining === 0 },
                    { icon: Star, text: '3 Wonder Twin Sessions', done: wonderTwinSessionsRemaining === 0 },
                    { icon: Percent, text: '10% Discount on Retreats & Masterminds', done: true },
                    { icon: Crown, text: 'Full App & Community Access', done: true },
                    { icon: MessageSquare, text: 'SDC Lounge Access', done: true },
                    { icon: Sparkles, text: 'Hot Seat & Concierge Requests', done: true },
                  ].map((benefit, i) => (
                    <div key={benefit.text} className="flex items-center gap-3 p-2">
                      {benefit.done ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <benefit.icon className="h-5 w-5 text-terracotta flex-shrink-0" />
                      )}
                      <span className={`text-sm ${benefit.done ? 'text-gray-600' : 'text-forest font-medium'}`}>
                        {benefit.text}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Chemistry Sessions Tab */}
          <TabsContent value="chemistry">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Chemistry Sessions</CardTitle>
                <CardDescription>
                  Meet with 3 different coaches for 15-minute chemistry sessions to find your perfect match.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Completed Chemistry Sessions */}
                {hubData?.chemistry_sessions?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium text-forest mb-3">Completed Sessions ({hubData.chemistry_sessions.length}/3)</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      {hubData.chemistry_sessions.map((session, i) => {
                        const coach = coaches.find(c => c.id === session.coach_id);
                        return (
                          <Card key={session.id || `${session.coach_id}-${i}`} className="border-green-200 bg-green-50/50">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="h-12 w-12 rounded-full overflow-hidden bg-sand">
                                  {coach?.image_url ? (
                                    <img src={coach.image_url} alt={coach?.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <User className="h-6 w-6 text-gray-400 m-3" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-forest">{coach?.name || 'Coach'}</p>
                                  <p className="text-xs text-green-600 flex items-center gap-1">
                                    <CheckCircle2 className="h-3 w-3" /> Completed
                                  </p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Available Coaches for Chemistry */}
                {chemistrySessionsRemaining > 0 ? (
                  <div>
                    <h3 className="font-medium text-forest mb-3">
                      Book Chemistry Session ({chemistrySessionsRemaining} remaining)
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Select a coach below to schedule a 15-minute chemistry session via Calendly.
                    </p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {availableForChemistry.slice(0, 12).map((coach) => (
                        <Card key={coach.id} className="card-base hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="h-14 w-14 rounded-full overflow-hidden bg-sand flex-shrink-0">
                                {coach.image_url ? (
                                  <img src={coach.image_url} alt={coach.name} className="w-full h-full object-cover" />
                                ) : (
                                  <User className="h-7 w-7 text-gray-400 m-3.5" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-forest truncate">{coach.name}</p>
                                <p className="text-xs text-gray-500 truncate">{coach.title}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-3">
                              {toStringArray(coach.specialties).slice(0, 2).map((s, i) => (
                                <Badge key={s} variant="secondary" className="text-xs bg-sand/50">
                                  {s}
                                </Badge>
                              ))}
                            </div>
                            <Button 
                              size="sm" 
                              className="w-full btn-secondary"
                              onClick={() => handleBookChemistry(coach.id)}
                              disabled={bookingCoach === coach.id}
                            >
                              {bookingCoach === coach.id ? (
                                <span className="flex items-center gap-2">
                                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  Booking...
                                </span>
                              ) : (
                                <>
                                  <Clock className="mr-2 h-4 w-4" />
                                  15-min Chemistry
                                </>
                              )}
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-green-50 rounded-xl">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <h3 className="font-heading text-xl font-semibold text-forest mb-2">All Chemistry Sessions Complete!</h3>
                    <p className="text-gray-600 mb-4">You've met with 3 coaches. Now it's time to choose your coach.</p>
                    {!coachSelected && (
                      <Button className="btn-primary" onClick={() => setSelectCoachDialog(true)}>
                        Select Your Coach <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coaching Sessions Tab */}
          <TabsContent value="coaching">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Coaching Sessions</CardTitle>
                <CardDescription>
                  12 one-hour sessions with your selected coach.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {coachSelected && selectedCoachData ? (
                  <div>
                    {/* Selected Coach Card */}
                    <div className="bg-forest text-white rounded-xl p-6 mb-6">
                      <div className="flex items-center gap-4">
                        <div className="h-20 w-20 rounded-full overflow-hidden border-4 border-white/20">
                          {selectedCoachData.image_url ? (
                            <img src={selectedCoachData.image_url} alt={selectedCoachData.name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-10 w-10 text-white/50 m-5" />
                          )}
                        </div>
                        <div className="flex-1">
                          <Badge className="bg-terracotta text-white mb-2">Your Coach</Badge>
                          <h3 className="font-heading text-2xl font-bold">{selectedCoachData.name}</h3>
                          <p className="text-white/80">{selectedCoachData.title}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-4xl font-bold">{coachSessionsRemaining}</p>
                          <p className="text-white/70 text-sm">Sessions Left</p>
                        </div>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="mb-6">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-600">Session Progress</span>
                        <span className="font-medium text-forest">{12 - coachSessionsRemaining} of 12 complete</span>
                      </div>
                      <Progress value={((12 - coachSessionsRemaining) / 12) * 100} className="h-3" />
                    </div>

                    {/* Book Session Button */}
                    {coachSessionsRemaining > 0 ? (
                      <Button className="w-full btn-primary h-12" onClick={handleBookCoachSession}>
                        <Calendar className="mr-2 h-5 w-5" />
                        Book 1-Hour Session with {selectedCoachData.name.split(' ')[0]}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <div className="text-center py-6 bg-green-50 rounded-xl">
                        <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
                        <p className="font-medium text-forest">All coaching sessions completed!</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Target className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-semibold text-forest mb-2">Select Your Coach First</h3>
                    <p className="text-gray-600 mb-4">
                      Complete your 3 chemistry sessions to unlock coach selection.
                    </p>
                    {chemistrySessionsUsed < 3 ? (
                      <p className="text-sm text-terracotta">
                        {3 - chemistrySessionsUsed} more chemistry session(s) needed
                      </p>
                    ) : (
                      <Button className="btn-secondary" onClick={() => setSelectCoachDialog(true)}>
                        Select Your Coach
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wonder Twin Sessions Tab */}
          <TabsContent value="wondertwin">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest">Wonder Twin Sessions</CardTitle>
                <CardDescription>
                  3 transformational sessions with Crystal & Dr. Nicole together.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-br from-forest to-terracotta text-white rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-20 rounded-full overflow-hidden border-4 border-white/20 bg-white/10">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_volition-membership/artifacts/wdy6rj8w_crystal%20and%20drnicole.png" 
                        alt="Crystal & Dr. Nicole"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <Badge className="bg-white/20 text-white mb-2">Wonder Twins</Badge>
                      <h3 className="font-heading text-2xl font-bold">Crystal & Dr. Nicole</h3>
                      <p className="text-white/80">Co-Founders, Volition Enterprises</p>
                    </div>
                    <div className="text-right">
                      <p className="text-4xl font-bold">{wonderTwinSessionsRemaining}</p>
                      <p className="text-white/70 text-sm">Sessions Left</p>
                    </div>
                  </div>
                </div>

                {/* Progress */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Session Progress</span>
                    <span className="font-medium text-forest">{3 - wonderTwinSessionsRemaining} of 3 complete</span>
                  </div>
                  <Progress value={((3 - wonderTwinSessionsRemaining) / 3) * 100} className="h-3" />
                </div>

                {/* Book Session */}
                {wonderTwinSessionsRemaining > 0 ? (
                  <Button className="w-full btn-secondary h-12" onClick={handleBookWonderTwin}>
                    <Star className="mr-2 h-5 w-5" />
                    Book Wonder Twin Session
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <div className="text-center py-6 bg-green-50 rounded-xl">
                    <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
                    <p className="font-medium text-forest">All Wonder Twin sessions completed!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* VIP Perks Tab */}
          <TabsContent value="perks">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Percent className="h-5 w-5 text-terracotta" />
                    10% VIP Discount
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    As an SDC member, you receive an exclusive 10% discount on:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>All Retreats (Costa Rica & future)</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>Mastermind Activations</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>VIP Events & Workshops</span>
                    </li>
                  </ul>
                  <div className="mt-6 p-4 bg-terracotta/10 rounded-xl">
                    <p className="text-sm text-forest font-medium">Your Discount Code:</p>
                    <p className="text-2xl font-bold text-terracotta tracking-wider">SDC-VIP-10</p>
                    <p className="text-xs text-gray-500 mt-1">Apply at checkout or mention during booking</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                    <Crown className="h-5 w-5 text-terracotta" />
                    Full Access Unlocked
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Your SDC membership includes complete access to:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>All Community Spaces</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>Full Video Library</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>Weekly Gems & Prompts</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>Full Coach Directory</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      <span>VIP Events Calendar</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SDC Lounge Tab */}
          <TabsContent value="lounge">
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-terracotta" />
                  SDC Lounge
                </CardTitle>
                <CardDescription>
                  Your exclusive VIP space for hot seats, concierge requests, and VIP events.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-sand/30 rounded-xl">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-lg bg-terracotta/10 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-terracotta" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Hot Seat Sessions</p>
                        <p className="text-xs text-gray-500">Submit topics for live coaching</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-sand/30 rounded-xl">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-10 w-10 rounded-lg bg-forest/10 flex items-center justify-center">
                        <Heart className="h-5 w-5 text-forest" />
                      </div>
                      <div>
                        <p className="font-medium text-forest">Concierge Service</p>
                        <p className="text-xs text-gray-500">Personal assistance & introductions</p>
                      </div>
                    </div>
                  </div>
                </div>
                <Link to="/sdc-lounge">
                  <Button className="w-full btn-primary">
                    Enter SDC Lounge <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Coach Selection Dialog */}
        <Dialog open={selectCoachDialog} onOpenChange={setSelectCoachDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-forest">Select Your Coach</DialogTitle>
              <DialogDescription>
                Based on your chemistry sessions, choose the coach you'd like to work with for your 12 coaching sessions.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {hubData?.chemistry_sessions?.map((session, i) => {
                const coach = coaches.find(c => c.id === session.coach_id);
                if (!coach) return null;
                return (
                  <div 
                    key={session.id || `${session.coach_id}-${i}`}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedCoach === coach.id 
                        ? 'border-terracotta bg-terracotta/5' 
                        : 'border-gray-200 hover:border-terracotta/50'
                    }`}
                    onClick={() => setSelectedCoach(coach.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 rounded-full overflow-hidden bg-sand">
                        {coach.image_url ? (
                          <img src={coach.image_url} alt={coach.name} className="w-full h-full object-cover" />
                        ) : (
                          <User className="h-8 w-8 text-gray-400 m-4" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-heading font-semibold text-forest">{coach.name}</p>
                        <p className="text-sm text-gray-500">{coach.title}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {toStringArray(coach.specialties).slice(0, 3).map((s, j) => (
                            <Badge key={j} variant="secondary" className="text-xs">
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      {selectedCoach === coach.id && (
                        <CheckCircle2 className="h-6 w-6 text-terracotta" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectCoachDialog(false)}>
                Cancel
              </Button>
              <Button 
                className="btn-primary" 
                onClick={handleSelectCoach}
                disabled={!selectedCoach}
              >
                Confirm Selection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SDCHubPage;
